import React, { useState, useEffect } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import Preloader from "../../components/Preloader";

const Institutes = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("add"); // "add" or "modify"

  // State for adding a new institute
  const [newInstitute, setNewInstitute] = useState({
    name: "",
    domains: "",
    street: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
    timezone: "",
    type: "university",
    billingType: "individual",
    stripeCustomerId: "",
    contractStart: "",
    contractEnd: "",
    isActive: true,
  });

  // State for modifying institutes
  const [institutes, setInstitutes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [details, setDetails] = useState({ ...newInstitute });

  // State for terms
  const [terms, setTerms] = useState([]);
  const [newTerm, setNewTerm] = useState({
    name: "",
    type: "semester",
    startDate: "",
    endDate: "",
    registrationStart: "",
    registrationEnd: "",
    isActive: true,
  });
  const [termDetails, setTermDetails] = useState({ ...newTerm });
  const [editingTermId, setEditingTermId] = useState("");

  // Fetch all institutes
  const fetchInstitutes = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "institutes"));
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setInstitutes(data);
    } catch (err) {
      console.error("Error fetching institutes:", err);
      alert("Failed to fetch institutes.");
    } finally {
      setLoading(false);
    }
  };

  // When switching to modify, load institutes
  useEffect(() => {
    if (activeTab === "modify") fetchInstitutes();
  }, [activeTab]);

  // Generic change handler for institute forms
  const handleChange = (e, isDetail = false) => {
    const { name, value, type, checked } = e.target;
    const setter = isDetail ? setDetails : setNewInstitute;
    const prev = isDetail ? details : newInstitute;
    setter({ ...prev, [name]: type === "checkbox" ? checked : value });
  };

  // Add a new institute
  const handleAdd = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ref = doc(collection(db, "institutes"));
      await setDoc(ref, {
        name: newInstitute.name,
        domains: newInstitute.domains.split(",").map((d) => d.trim()).filter(Boolean),
        address: {
          street: newInstitute.street,
          city: newInstitute.city,
          state: newInstitute.state,
          country: newInstitute.country,
          postalCode: newInstitute.postalCode,
          timezone: newInstitute.timezone,
        },
        type: newInstitute.type,
        billingType: newInstitute.billingType,
        stripeCustomerId: newInstitute.stripeCustomerId || null,
        contractStart: newInstitute.contractStart ? new Date(newInstitute.contractStart) : null,
        contractEnd: newInstitute.contractEnd ? new Date(newInstitute.contractEnd) : null,
        isActive: newInstitute.isActive,
        createdBy: auth.currentUser?.uid || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      alert("Institute added successfully.");
      setNewInstitute({
        name: "",
        domains: "",
        street: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
        timezone: "",
        type: "university",
        billingType: "individual",
        stripeCustomerId: "",
        contractStart: "",
        contractEnd: "",
        isActive: true,
      });
    } catch (err) {
      console.error("Error adding institute:", err);
      alert("Failed to add institute.");
    } finally {
      setLoading(false);
    }
  };

  // Load institute details and its terms for editing
  const handleEdit = async (id) => {
    setLoading(true);
    try {
      const docRef = doc(db, "institutes", id);
      const snap = await getDoc(docRef);
      if (!snap.exists()) return alert("Institute not found.");
      const d = snap.data();
      setDetails({
        name: d.name || "",
        domains: (d.domains || []).join(", "),
        street: d.address?.street || "",
        city: d.address?.city || "",
        state: d.address?.state || "",
        country: d.address?.country || "",
        postalCode: d.address?.postalCode || "",
        timezone: d.address?.timezone || "",
        type: d.type || "university",
        billingType: d.billingType || "individual",
        stripeCustomerId: d.stripeCustomerId || "",
        contractStart: d.contractStart ? d.contractStart.toDate().toISOString().substr(0, 10) : "",
        contractEnd: d.contractEnd ? d.contractEnd.toDate().toISOString().substr(0, 10) : "",
        isActive: d.isActive ?? true,
      });
      setSelectedId(id);
      fetchTerms(id);
    } catch (err) {
      console.error("Error loading institute:", err);
      alert("Failed to load institute.");
    } finally {
      setLoading(false);
    }
  };

  // Update institute details
  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedId) return alert("No institute selected.");
    setLoading(true);
    try {
      const docRef = doc(db, "institutes", selectedId);
      await updateDoc(docRef, {
        name: details.name,
        domains: details.domains.split(",").map((d) => d.trim()).filter(Boolean),
        address: {
          street: details.street,
          city: details.city,
          state: details.state,
          country: details.country,
          postalCode: details.postalCode,
          timezone: details.timezone,
        },
        type: details.type,
        billingType: details.billingType,
        stripeCustomerId: details.stripeCustomerId || null,
        contractStart: details.contractStart ? new Date(details.contractStart) : null,
        contractEnd: details.contractEnd ? new Date(details.contractEnd) : null,
        isActive: details.isActive,
        updatedAt: serverTimestamp(),
      });
      alert("Institute updated successfully.");
      fetchInstitutes();
      setSelectedId("");
    } catch (err) {
      console.error("Error updating institute:", err);
      alert("Failed to update institute.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch terms for a given institute
  const fetchTerms = async (instituteId) => {
    try {
      const termSnap = await getDocs(collection(db, "institutes", instituteId, "terms"));
      const termData = termSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTerms(termData);
    } catch (err) {
      console.error("Error fetching terms:", err);
      alert("Failed to fetch terms.");
    }
  };

  // Generic change handler for term forms
  const handleTermChange = (e, isEdit = false) => {
    const { name, value, type, checked } = e.target;
    const setter = isEdit ? setTermDetails : setNewTerm;
    const prev = isEdit ? termDetails : newTerm;
    setter({ ...prev, [name]: type === "checkbox" ? checked : value });
  };

  // Add a new term
  const handleAddTerm = async (e) => {
    e.preventDefault();
    if (!selectedId) return alert("Select an institute first.");
    setLoading(true);
    try {
      const termRef = doc(collection(db, "institutes", selectedId, "terms"));
      await setDoc(termRef, {
        instituteId: selectedId,
        name: newTerm.name,
        type: newTerm.type,
        startDate: newTerm.startDate ? new Date(newTerm.startDate) : null,
        endDate: newTerm.endDate ? new Date(newTerm.endDate) : null,
        registrationStart: newTerm.registrationStart ? new Date(newTerm.registrationStart) : null,
        registrationEnd: newTerm.registrationEnd ? new Date(newTerm.registrationEnd) : null,
        isActive: newTerm.isActive,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      alert("Term added successfully.");
      setNewTerm({
        name: "",
        type: "semester",
        startDate: "",
        endDate: "",
        registrationStart: "",
        registrationEnd: "",
        isActive: true,
      });
      fetchTerms(selectedId);
    } catch (err) {
      console.error("Error adding term:", err);
      alert("Failed to add term.");
    } finally {
      setLoading(false);
    }
  };

  // Load term details into edit form
  const handleTermEditClick = (termId) => {
    const term = terms.find((t) => t.id === termId);
    if (!term) return;
    setTermDetails({
      name: term.name || "",
      type: term.type || "semester",
      startDate: term.startDate ? term.startDate.toDate().toISOString().substr(0, 10) : "",
      endDate: term.endDate ? term.endDate.toDate().toISOString().substr(0, 10) : "",
      registrationStart: term.registrationStart ? term.registrationStart.toDate().toISOString().substr(0, 10) : "",
      registrationEnd: term.registrationEnd ? term.registrationEnd.toDate().toISOString().substr(0, 10) : "",
      isActive: term.isActive ?? true,
    });
    setEditingTermId(termId);
  };

  // Update an existing term
  const handleUpdateTerm = async (e) => {
    e.preventDefault();
    if (!selectedId || !editingTermId) return;
    setLoading(true);
    try {
      const termDocRef = doc(db, "institutes", selectedId, "terms", editingTermId);
      await updateDoc(termDocRef, {
        name: termDetails.name,
        type: termDetails.type,
        startDate: termDetails.startDate ? new Date(termDetails.startDate) : null,
        endDate: termDetails.endDate ? new Date(termDetails.endDate) : null,
        registrationStart: termDetails.registrationStart ? new Date(termDetails.registrationStart) : null,
        registrationEnd: termDetails.registrationEnd ? new Date(termDetails.registrationEnd) : null,
        isActive: termDetails.isActive,
        updatedAt: serverTimestamp(),
      });
      alert("Term updated successfully.");
      setEditingTermId("");
      setTermDetails({ ...newTerm });
      fetchTerms(selectedId);
    } catch (err) {
      console.error("Error updating term:", err);
      alert("Failed to update term.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="institutes-section"
      className="flex flex-col items-center min-h-screen p-8"
      style={{ backgroundColor: "var(--section-bg)", color: "var(--text-color)" }}
    >
      {loading && <Preloader />}

      <ul className="flex border-b border-[var(--border-color)] w-full max-w-4xl mb-4">
        {['add','modify'].map((tab) => (
          <li key={tab} className="mr-4">
            <button
              className={`px-4 py-2 -mb-px border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-[var(--primary-color)] text-[var(--primary-color)]'
                  : 'border-transparent hover:border-[var(--border-color)] text-[var(--muted-color)]'}
              `}
              onClick={() => { setActiveTab(tab); setSelectedId(''); }}
            >{tab === 'add' ? 'Add Institute' : 'Modify Institute'}</button>
          </li>
        ))}
      </ul>

      <div className="w-full max-w-4xl">
        {/* Add Institute */}
        {activeTab === 'add' && (
          <form onSubmit={handleAdd} className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow">
            {/* Name & Domains */}
            <div className="flex flex-col md:flex-row md:items-center">
              <label htmlFor="name" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Name:</label>
              <input
                id="name"
                name="name"
                value={newInstitute.name}
                onChange={handleChange}
                className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                required
              />
            </div>
            <div className="flex flex-col md:flex-row md:items-center">
              <label htmlFor="domains" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Domains (comma-separated):</label>
              <input
                id="domains"
                name="domains"
                value={newInstitute.domains}
                onChange={handleChange}
                className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                required
              />
            </div>

            {/* Address Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['street','city','state','country','postalCode','timezone'].map((field) => (
                <div key={field} className="flex flex-col">
                  <label htmlFor={field} className="mb-1 capitalize">{field}:</label>
                  <input
                    id={field}
                    name={field}
                    value={newInstitute[field]}
                    onChange={handleChange}
                    className="p-2 border border-[var(--border-color)] rounded"
                    required={['country','timezone'].includes(field)}
                  />
                </div>
              ))}
            </div>

            {/* Type & Billing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <label htmlFor="type" className="mb-1">Type:</label>
                <select
                  id="type"
                  name="type"
                  value={newInstitute.type}
                  onChange={handleChange}
                  className="p-2 border border-[var(--border-color)] rounded"
                  required
                >
                  <option value="university">University</option>
                  <option value="college">College</option>
                  <option value="school">School</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>
              <div className="flex flex-col">
                <label htmlFor="billingType" className="mb-1">Billing Type:</label>
                <select
                  id="billingType"
                  name="billingType"
                  value={newInstitute.billingType}
                  onChange={handleChange}
                  className="p-2 border border-[var(--border-color)] rounded"
                  required
                >
                  <option value="individual">Individual</option>
                  <option value="institutional">Institutional</option>
                </select>
              </div>
            </div>

            {/* Stripe Customer ID for institutional */}
            {newInstitute.billingType === 'institutional' && (
              <div className="flex flex-col md:flex-row md:items-center">
                <label htmlFor="stripeCustomerId" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Stripe Customer ID:</label>
                <input
                  id="stripeCustomerId"
                  name="stripeCustomerId"
                  value={newInstitute.stripeCustomerId}
                  onChange={handleChange}
                  className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                  required
                />
              </div>
            )}

            {/* Dates & Active */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="flex flex-col">
                <label htmlFor="contractStart" className="mb-1">Contract Start:</label>
                <input
                  type="date"
                  id="contractStart"
                  name="contractStart"
                  value={newInstitute.contractStart}
                  onChange={handleChange}
                  className="p-2 border border-[var(--border-color)] rounded"
                />
              </div>
              <div className="flex flex-col">
                <label htmlFor="contractEnd" className="mb-1">Contract End:</label>
                <input
                  type="date"
                  id="contractEnd"
                  name="contractEnd"
                  value={newInstitute.contractEnd}
                  onChange={handleChange}
                  className="p-2 border border-[var(--border-color)] rounded"
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={newInstitute.isActive}
                  onChange={handleChange}
                />
                <label htmlFor="isActive" className="ml-2">Active</label>
              </div>
            </div>

            <div className="flex justify-end">
              <button type="submit" className="px-4 py-2 bg-[var(--primary-color)] text-white rounded">Add Institute</button>
            </div>
          </form>
        )}

        {/* Modify Institute */}
        {activeTab === 'modify' && (
          <>
            <table className="min-w-full border border-[var(--border-color)]">
              <thead className="bg-[var(--alt-bg)]">
                <tr>
                  <th className="py-2 px-4 text-left">Name</th>
                  <th className="py-2 px-4 text-left">Domains</th>
                  <th className="py-2 px-4 text-left">Type</th>
                  <th className="py-2 px-4 text-left">Billing</th>
                  <th className="py-2 px-4 text-left">Active</th>
                  <th className="py-2 px-4 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {institutes.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="py-2 px-4 text-center text-[var(--muted-color)]">No institutes found.</td>
                  </tr>
                ) : (
                  institutes.map((ins) => (
                    <tr key={ins.id} className="hover:bg-[var(--card-hover-bg)]">
                      <td className="py-2 px-4">{ins.name}</td>
                      <td className="py-2 px-4">{(ins.domains || []).join(", ")}</td>
                      <td className="py-2 px-4">{ins.type}</td>
                      <td className="py-2 px-4">{ins.billingType}</td>
                      <td className="py-2 px-4">{ins.isActive ? 'Yes' : 'No'}</td>
                      <td className="py-2 px-4">
                        <button onClick={() => handleEdit(ins.id)} className="px-3 py-1 bg-[var(--primary-color)] text-white rounded text-sm">Edit</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {selectedId && (
              <>
                <form onSubmit={handleUpdate} className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow mt-4">
                  <h3 className="text-xl font-bold">Edit Institute</h3>

                  {/* Reuse add fields with 'details' state */}
                  <div className="flex flex-col md:flex-row md:items-center">
                    <label htmlFor="nameEdit" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Name:</label>
                    <input
                      id="nameEdit"
                      name="name"
                      value={details.name}
                      onChange={(e) => handleChange(e, true)}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                      required
                    />
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center">
                    <label htmlFor="domainsEdit" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Domains (comma-separated):</label>
                    <input
                      id="domainsEdit"
                      name="domains"
                      value={details.domains}
                      onChange={(e) => handleChange(e, true)}
                      className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {['street','city','state','country','postalCode','timezone'].map((field) => (
                      <div key={field} className="flex flex-col">
                        <label htmlFor={`${field}Edit`} className="mb-1 capitalize">{field}:</label>
                        <input
                          id={`${field}Edit`}
                          name={field}
                          value={details[field]}
                          onChange={(e) => handleChange(e, true)}
                          className="p-2 border border-[var(--border-color)] rounded"
                          required={['country','timezone'].includes(field)}
                        />
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col">
                      <label htmlFor="typeEdit" className="mb-1">Type:</label>
                      <select
                        id="typeEdit"
                        name="type"
                        value={details.type}
                        onChange={(e) => handleChange(e, true)}
                        className="p-2 border border-[var(--border-color)] rounded"
                        required
                      >
                        <option value="university">University</option>
                        <option value="college">College</option>
                        <option value="school">School</option>
                        <option value="enterprise">Enterprise</option>
                      </select>
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="billingTypeEdit" className="mb-1">Billing Type:</label>
                      <select
                        id="billingTypeEdit"
                        name="billingType"
                        value={details.billingType}
                        onChange={(e) => handleChange(e, true)}
                        className="p-2 border border-[var(--border-color)] rounded"
                        required
                      >
                        <option value="individual">Individual</option>
                        <option value="institutional">Institutional</option>
                      </select>
                    </div>
                  </div>

                  {details.billingType === 'institutional' && (
                    <div className="flex flex-col md:flex-row md:items-center">
                      <label htmlFor="stripeCustomerIdEdit" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Stripe Customer ID:</label>
                      <input
                        id="stripeCustomerIdEdit"
                        name="stripeCustomerId"
                        value={details.stripeCustomerId}
                        onChange={(e) => handleChange(e, true)}
                        className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                        required
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                    <div className="flex flex-col">
                      <label htmlFor="contractStartEdit" className="mb-1">Contract Start:</label>
                      <input
                        type="date"
                        id="contractStartEdit"
                        name="contractStart"
                        value={details.contractStart}
                        onChange={(e) => handleChange(e, true)}
                        className="p-2 border border-[var(--border-color)] rounded"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label htmlFor="contractEndEdit" className="mb-1">Contract End:</label>
                      <input
                        type="date"
                        id="contractEndEdit"
                        name="contractEnd"
                        value={details.contractEnd}
                        onChange={(e) => handleChange(e, true)}
                        className="p-2 border border-[var(--border-color)] rounded"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isActiveEdit"
                        name="isActive"
                        checked={details.isActive}
                        onChange={(e) => handleChange(e, true)}
                      />
                      <label htmlFor="isActiveEdit" className="ml-2">Active</label>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button type="submit" className="px-4 py-2 bg-[var(--primary-color)] text-white rounded">Save Changes</button>
                  </div>
                </form>

                {/* Terms Management */}
                <div className="mt-8">
                  <h3 className="text-xl font-bold">Add Term</h3>
                  <form onSubmit={handleAddTerm} className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow">
                    <div className="flex flex-col md:flex-row md:items-center">
                      <label htmlFor="termName" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Term Name:</label>
                      <input
                        type="text"
                        id="termName"
                        name="name"
                        value={newTerm.name}
                        onChange={handleTermChange}
                        className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                        required
                      />
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center">
                      <label htmlFor="termType" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">Type:</label>
                      <select
                        id="termType"
                        name="type"
                        value={newTerm.type}
                        onChange={handleTermChange}
                        className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                        required
                      >
                        <option value="semester">Semester</option>
                        <option value="trimester">Trimester</option>
                        <option value="quarter">Quarter</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <label htmlFor="startDate" className="mb-1">Start Date:</label>
                        <input
                          type="date"
                          id="startDate"
                          name="startDate"
                          value={newTerm.startDate}
                          onChange={handleTermChange}
                          className="p-2 border border-[var(--border-color)] rounded"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label htmlFor="endDate" className="mb-1">End Date:</label>
                        <input
                          type="date"
                          id="endDate"
                          name="endDate"
                          value={newTerm.endDate}
                          onChange={handleTermChange}
                          className="p-2 border border-[var(--border-color)] rounded"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex flex-col">
                        <label htmlFor="registrationStart" className="mb-1">Registration Start:</label>
                        <input
                          type="date"
                          id="registrationStart"
                          name="registrationStart"
                          value={newTerm.registrationStart}
                          onChange={handleTermChange}
                          className="p-2 border border-[var(--border-color)] rounded"
                        />
                      </div>
                      <div className="flex flex-col">
                        <label htmlFor="registrationEnd" className="mb-1">Registration End:</label>
                        <input
                          type="date"
                          id="registrationEnd"
                          name="registrationEnd"
                          value={newTerm.registrationEnd}
                          onChange={handleTermChange}
                          className="p-2 border border-[var(--border-color)] rounded"
                        />
                      </div>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="termIsActive"
                        name="isActive"
                        checked={newTerm.isActive}
                        onChange={handleTermChange}
                      />
                      <label htmlFor="termIsActive" className="ml-2">Active</label>
                    </div>
                    <div className="flex justify-end">
                      <button type="submit" className="px-4 py-2 bg-[var(--primary-color)] text-white rounded">Add Term</button>
                    </div>
                  </form>

                  <h3 className="text-xl font-bold mt-6">Existing Terms</h3>
                  {terms.length === 0 ? (
                    <p className="text-[var(--muted-color)]">No terms found.</p>
                  ) : (
                    <table className="min-w-full border border-[var(--border-color)] mt-2">
                      <thead className="bg-[var(--alt-bg)]">
                        <tr>
                          <th className="py-2 px-4">Name</th>
                          <th className="py-2 px-4">Type</th>
                          <th className="py-2 px-4">Start</th>
                          <th className="py-2 px-4">End</th>
                          <th className="py-2 px-4">Reg Start</th>
                          <th className="py-2 px-4">Reg End</th>
                          <th className="py-2 px-4">Active</th>
                          <th className="py-2 px-4">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {terms.map((term) => (
                          <tr key={term.id} className="hover:bg-[var(--card-hover-bg)]">
                            <td className="py-2 px-4">{term.name}</td>
                            <td className="py-2 px-4">{term.type}</td>
                            <td className="py-2 px-4">{term.startDate ? new Date(term.startDate.seconds * 1000).toLocaleDateString() : ""}</td>
                            <td className="py-2 px-4">{term.endDate ? new Date(term.endDate.seconds * 1000).toLocaleDateString() : ""}</td>
                            <td className="py-2 px-4">{term.registrationStart ? new Date(term.registrationStart.seconds * 1000).toLocaleDateString() : ""}</td>
                            <td className="py-2 px-4">{term.registrationEnd ? new Date(term.registrationEnd.seconds * 1000).toLocaleDateString() : ""}</td>
                            <td className="py-2 px-4">{term.isActive ? "Yes" : "No"}</td>
                            <td className="py-2 px-4">
                              <button onClick={() => handleTermEditClick(term.id)} className="px-3 py-1 bg-[var(--primary-color)] text-white rounded text-sm">Edit</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {editingTermId && (
                    <form onSubmit={handleUpdateTerm} className="flex flex-col space-y-4 bg-[var(--card-bg)] p-4 rounded shadow mt-4">
                      <h4 className="font-bold">Edit Term</h4>

                      {/* Term Name */}
                      <div className="flex flex-col md:flex-row md:items-center">
                        <label htmlFor="editTermName" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">
                          Term Name:
                        </label>
                        <input
                          type="text"
                          id="editTermName"
                          name="name"
                          value={termDetails.name}
                          onChange={(e) => handleTermChange(e, true)}
                          className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                          required
                        />
                      </div>

                      {/* Term Type */}
                      <div className="flex flex-col md:flex-row md:items-center">
                        <label htmlFor="editTermType" className="md:w-1/3 mb-2 md:mb-0 md:text-right md:pr-4">
                          Type:
                        </label>
                        <select
                          id="editTermType"
                          name="type"
                          value={termDetails.type}
                          onChange={(e) => handleTermChange(e, true)}
                          className="p-2 border border-[var(--border-color)] rounded w-full md:w-2/3"
                          required
                        >
                          <option value="semester">Semester</option>
                          <option value="trimester">Trimester</option>
                          <option value="quarter">Quarter</option>
                          <option value="custom">Custom</option>
                        </select>
                      </div>

                      {/* Dates */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <label htmlFor="editStartDate" className="mb-1">Start Date:</label>
                          <input
                            type="date"
                            id="editStartDate"
                            name="startDate"
                            value={termDetails.startDate}
                            onChange={(e) => handleTermChange(e, true)}
                            className="p-2 border border-[var(--border-color)] rounded"
                            required
                          />
                        </div>
                        <div className="flex flex-col">
                          <label htmlFor="editEndDate" className="mb-1">End Date:</label>
                          <input
                            type="date"
                            id="editEndDate"
                            name="endDate"
                            value={termDetails.endDate}
                            onChange={(e) => handleTermChange(e, true)}
                            className="p-2 border border-[var(--border-color)] rounded"
                            required
                          />
                        </div>
                      </div>

                      {/* Registration Window */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col">
                          <label htmlFor="editRegistrationStart" className="mb-1">Registration Start:</label>
                          <input
                            type="date"
                            id="editRegistrationStart"
                            name="registrationStart"
                            value={termDetails.registrationStart}
                            onChange={(e) => handleTermChange(e, true)}
                            className="p-2 border border-[var(--border-color)] rounded"
                          />
                        </div>
                        <div className="flex flex-col">
                          <label htmlFor="editRegistrationEnd" className="mb-1">Registration End:</label>
                          <input
                            type="date"
                            id="editRegistrationEnd"
                            name="registrationEnd"
                            value={termDetails.registrationEnd}
                            onChange={(e) => handleTermChange(e, true)}
                            className="p-2 border border-[var(--border-color)] rounded"
                          />
                        </div>
                      </div>

                      {/* Active Checkbox */}
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="editTermIsActive"
                          name="isActive"
                          checked={termDetails.isActive}
                          onChange={(e) => handleTermChange(e, true)}
                        />
                        <label htmlFor="editTermIsActive" className="ml-2">Active</label>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end">
                        <button type="submit" className="px-4 py-2 bg-[var(--primary-color)] text-white rounded">
                          Save Term
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default Institutes;